import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { colorsTheme } from 'resources/theme/styled/colors';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';

// components
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import FieldIssueTable, {
  FieldIssueModel,
} from 'components/Tables/TableTemplates/IntakeForm/FieldIssueTable';
import { IconSVG, SectionIntro, Text } from '@ui';
import { ReactComponent as AlertIcon } from 'resources/icons/remix-icons/alert-fill.svg';
import { ReactComponent as CircleIcon } from 'resources/icons/remix-icons/checkbox-blank-circle-fill.svg';

type CommentsType = 'open' | 'resolved' | 'all';

const ApplicationReviewComments = () => {
  const { t } = useTranslation('onboarding');

  const { applicationReview, navigateToComment } = useEntityIntakeFormContext();

  const onActionsClick = (key: 'view', comment: FieldIssueModel) => {
    if (key === 'view') {
      navigateToComment(comment.step, comment.fieldId);
    }
  };

  const fieldComments = useMemo(() => {
    return (
      <>
        {applicationReview?.fieldIssues.map((field) => {
          const { stepName, fieldComments } = field;
          const commentCount = fieldComments.length;
          const commentText =
            commentCount !== 1
              ? t('intake_form.review_field_issues.plural_comment')
              : t('intake_form.review_field_issues.single_comment');
          const sectionTitle = `${stepName} (${commentCount} ${commentText})`;
          return (
            <FieldIssueContent key={field.stepName}>
              <SectionIntro
                hideDivider
                titleVariant="h5"
                titleColor={colorsTheme.colorWhite}
                title={sectionTitle}
              />
              <FieldIssueTable
                data={field.fieldComments}
                total={field.fieldComments.length}
                onActionsClick={onActionsClick}
              />
            </FieldIssueContent>
          );
        })}
      </>
    );
  }, [applicationReview, t]);

  if (!applicationReview) {
    return null;
  }

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant="h4"
        title={
          <DivAlignCenter>
            {t('intake_form.application_review_comments.title')}
            <StyledIconSVG
              component={AlertIcon}
              color={colorsTheme.colorWarning}
            />
          </DivAlignCenter>
        }
      />
      <DescriptionText>
        {t('intake_form.application_review_comments.description')}
      </DescriptionText>
      <CommentsSection>
        <Comment type="open">
          {t('intake_form.application_review_comments.status.open')}{' '}
          {applicationReview.activeCommentsCount}
        </Comment>
        <CircleIconSVG component={CircleIcon} color={colorsTheme.colorLight} />
        <Comment type="resolved">
          {t('intake_form.application_review_comments.status.resolved')}{' '}
          {applicationReview.resolvedCommentsCount}
        </Comment>
        <CircleIconSVG component={CircleIcon} color={colorsTheme.colorLight} />
        <Comment type="all">
          {t('intake_form.application_review_comments.status.all')}{' '}
          {applicationReview.allCommentsCount}
        </Comment>
      </CommentsSection>
      <>{fieldComments}</>
    </>
  );
};

const CircleIconSVG = styled(IconSVG)`
  svg {
    width: 6px !important;
    height: 6px !important;
  }
`;

const StyledIconSVG = styled(IconSVG)`
  cursor: pointer;
  margin-left: ${({ theme }) => theme.marginXs};
`;

const CommentsSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.paddingXs};
  padding-bottom: ${({ theme }) => theme.paddingMd};
`;

const Comment = styled.div<{ type: CommentsType }>`
  color: ${({ type, theme }) => {
    switch (type) {
      case 'open':
        return theme.colorWarning;
      case 'resolved':
        return theme.colorSuccess;
      case 'all':
        return theme.colorWhite;
    }
  }};
`;

const FieldIssueContent = styled.div`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.marginMd};
  }
`;

const DescriptionText = styled(Text)`
  margin-bottom: ${({ theme }) => theme.marginXs};
`;

export default ApplicationReviewComments;
